import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  GitPullRequest,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ApiClient from "../../lib/apiClient";
import { isDeveloperCaoScout } from "../../lib/developer-cao-scout";
import {
  createScoutService,
  type ScoutRecord,
} from "../../lib/services/scouts";
import {
  buildUpcomingScouts,
  formatNextScoutTime,
  scheduleId,
  type UpcomingScout,
} from "../../lib/scout-upcoming";
import { FintheonNumericText } from "../numbers/FintheonNumericText";

export function FooterUpcomingScoutsPanel() {
  const { getAccessToken } = useAuth();
  const [scouts, setScouts] = useState<ScoutRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const service = useMemo(
    () => createScoutService(new ApiClient().withAuth(getAccessToken)),
    [getAccessToken],
  );

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      setScouts(await service.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Scouts unavailable");
    } finally {
      setIsLoading(false);
    }
  }, [service]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(), 60_000);
    window.addEventListener("fintheon:scout-created", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("fintheon:scout-created", refresh);
    };
  }, [refresh]);

  const upcoming = useMemo(() => buildUpcomingScouts(scouts), [scouts]);

  const toggleScout = useCallback(
    async (scout: ScoutRecord) => {
      const nextStatus = scout.status === "active" ? "paused" : "active";
      setBusyId(scout.id);
      try {
        const updated = await service.update(scout.id, {
          ...scout,
          status: nextStatus,
        });
        setScouts((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item)),
        );
      } finally {
        setBusyId(null);
      }
    },
    [service],
  );

  return (
    <div className="h-full overflow-y-auto px-3 py-2 text-[#f0ead6]">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#c79f4a]">
            Upcoming Scouts
          </p>
          <p className="mt-0.5 text-[10px] text-[#f0ead6]/40">
            Next 24h · user-scoped schedules
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refresh()}
          className="grid h-7 w-7 place-items-center rounded-[4px] border border-[#c79f4a]/14 text-[#f0ead6]/45 hover:bg-[#c79f4a]/8 hover:text-[#c79f4a]"
          title="Refresh upcoming Scouts"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${isLoading ? "animate-spin" : ""}`}
          />
        </button>
      </div>
      {error ? (
        <PanelMessage text={error} />
      ) : upcoming.length === 0 ? (
        <PanelMessage
          text={
            isLoading
              ? "Loading Scouts..."
              : "No scheduled Scouts in the next 24h."
          }
        />
      ) : (
        <div className="grid gap-x-4 md:grid-cols-2">
          {upcoming.map((item) => (
            <UpcomingRow
              key={`${item.scout.id}:${scheduleId(item.trigger)}`}
              item={item}
              busy={busyId === item.scout.id}
              onToggle={() => void toggleScout(item.scout)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UpcomingRow({
  item,
  busy,
  onToggle,
}: {
  item: UpcomingScout;
  busy: boolean;
  onToggle: () => void;
}) {
  const active = item.scout.status === "active";
  const Icon = iconForScout(item.scout);
  return (
    <div className="relative grid grid-cols-[18px_minmax(0,1fr)_78px_42px] items-center gap-2 py-2">
      <Icon className="h-3.5 w-3.5 text-[#c79f4a]/68" />
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-[11px] font-semibold text-[#f0ead6]/78">
            {item.scout.name}
          </span>
          {isDeveloperCaoScout(item.scout) ? <DeveloperChip /> : null}
        </div>
        <p className="mt-0.5 truncate text-[10px] text-[#f0ead6]/36">
          {item.trigger.schedule}
        </p>
      </div>
      <span className="justify-self-end text-right text-[10px] tabular-nums text-[#f0ead6]/52">
        <FintheonNumericText
          text={formatNextScoutTime(item.nextAt)}
          kind="date-time"
        />
      </span>
      <PullToggle
        checked={active}
        disabled={busy}
        onClick={onToggle}
        label={`${active ? "Pause" : "Activate"} ${item.scout.name}`}
      />
      <span className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-[linear-gradient(90deg,transparent,rgba(199,159,74,0.16),transparent)]" />
    </div>
  );
}

function DeveloperChip() {
  return (
    <span className="shrink-0 rounded-[3px] border border-[#ac5318]/35 bg-[#ac5318]/12 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-[#e0a16e]">
      Developer
    </span>
  );
}

function PullToggle({
  checked,
  disabled,
  onClick,
  label,
}: {
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={checked}
      disabled={disabled}
      onClick={onClick}
      className={`relative h-4 w-8 rounded-[10px] border transition-colors disabled:opacity-35 ${
        checked
          ? "border-[#c79f4a]/36 bg-[#c79f4a]/18"
          : "border-[#f0ead6]/14 bg-[#050402]"
      }`}
    >
      <span
        className={`absolute top-[2px] h-2.5 w-2.5 rounded-[6px] transition-transform ${
          checked
            ? "translate-x-[17px] bg-[#c79f4a]"
            : "translate-x-[3px] bg-[#f0ead6]/34"
        }`}
      />
    </button>
  );
}

function iconForScout(scout: ScoutRecord) {
  if (isDeveloperCaoScout(scout)) return GitPullRequest;
  if (scout.name.toLowerCase().includes("bug")) return AlertTriangle;
  return scout.toolPolicies.length > 0 ? CheckCircle2 : Clock;
}

function PanelMessage({ text }: { text: string }) {
  return <div className="px-1 py-4 text-[11px] text-[#f0ead6]/42">{text}</div>;
}
